import React from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTranslation } from 'react-i18next';
import Checkbox from '@mui/material/Checkbox';
import Radio from '@mui/material/Radio';
import makeStyles from '@mui/styles/makeStyles';
import { palette } from '../design-system/colors';

const useStyles = makeStyles(() => ({
    assessmentFormControlLabel: {
        padding: '12px',
        background: '#FFFFFF',
        boxShadow: '0px 4px 8px rgba(29, 30, 29, 0.06)',
        border: '2px solid rgba(#1D1E1D, 0)',
        borderRadius: '4px',
        transition: 'ease 0.2s all',

        '&:hover': {
            boxShadow:
                '0px 0px 2px rgba(0, 0, 0, 0.08), 0px 4px 24px rgba(29, 30, 29, 0.08)',
        },
        '&.active': {
            border: '0px 0px 4px rgba(29, 30, 29, 0.08)',
        },
        '&.selected': {
            border: '2px solid rgba(#1D1E1D, 0.8)',
        },
    },

    incorrect: {
        border: '2px solid ' + palette.lightroom.redLight,
        backgroundColor: palette.lightroom.redLightHigh,

        '& .MuiTypography-root': {
            color: palette.vibrant.red,
            fontWeight: 700,
        },

        '& .MuiRadio-root': {
            background:
                "url('/static/img/assessment/icon-incorrect.svg') no-repeat center center",
            color: 'transparent',
        },
    },

    somewhatCorrect: {
        border: '2px solid ' + palette.vibrant.gold,
        backgroundColor: palette.lightroom.goldLightHigh,

        '& .MuiTypography-root': {
            color: palette.vibrant.gold,
            fontWeight: 700,
        },

        '& .MuiRadio-root': {
            background:
                "url('/static/img/assessment/icon-somewhat-incorrect.svg') no-repeat center center",
            color: 'transparent',
        },
    },

    correct: {
        '& .MuiRadio-root': {
            background:
                "url('/static/img/assessment/icon-correct.svg') no-repeat center center",
            color: 'transparent',
        },
    },

    userCorrect: {
        border: '2px solid ' + palette.lightroom.aspargusLight,
        backgroundColor: palette.lightroom.aspargusLightHigh,

        '& .MuiTypography-root': {
            color: palette.evergreen.aspargus,
            fontWeight: 700,
        },
    },

    correctButIrrelevant: {
        border: '2px solid ' + palette.lightroom.heavyMetalLight,

        '& .MuiTypography-root': {
            color: palette.graphite.heavyMetal,
            fontWeight: 700,
        },

        '& .MuiRadio-root': {
            background:
                "url('/static/img/assessment/icon-correct-but-irrelevant.svg') no-repeat center center",
            color: 'transparent',
        },
    },
}));

export default observer(({ onChange, groupValue, value, text, answer }) => {
    const { t } = useTranslation('assessment');
    const classes = useStyles();

    if (!answer) {
        return (
            <FormControlLabel
                className={classnames([classes.assessmentFormControlLabel], {
                    selected: (groupValue || []).includes(value),
                })}
                control={
                    <Checkbox
                        checked={(groupValue || []).includes(value)}
                        onChange={(e) => onChange(e, value)}
                        color="primary"
                    />
                }
                label={t(text)}
            />
        );
    } else {
        return (
            <FormControlLabel
                className={classnames(
                    'Mui-disabled',
                    [classes.assessmentFormControlLabel],
                    {
                        [classes.correct]:
                            answer.correctAnswer[value] &&
                            answer.correctAnswer[value].score > 0,
                        [classes.userCorrect]:
                            answer.correctAnswer[value] &&
                            answer.userAnswer[value] &&
                            answer.correctAnswer[value].score > 0,
                        [classes.correctButIrrelevant]:
                            answer.correctAnswer[value] &&
                            answer.userAnswer[value] &&
                            answer.correctAnswer[value].score <= 0,
                        [classes.incorrect]:
                            answer.userAnswer[value] &&
                            !answer.correctAnswer[value],
                    }
                )}
                disabled={true}
                control={<Radio color="primary" />}
                label={t(text)}
            />
        );
    }
});
